import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Wrench, AlertCircle, Clock, CheckCircle, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EngineerDevices = ({ user }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam) {
      setFilter(filterParam);
    }
    loadDevices();
  }, [filter]);

  const loadDevices = async () => {
    try {
      const token = localStorage.getItem('token');
      const engineerId = user?.employee_id || 'ENG001';
      
      let url = `${API_URL}/api/trc/engineer/${engineerId}/devices`;
      
      // If filter is SOS, use different endpoint
      if (filter === 'sos') {
        url = `${API_URL}/api/trc/devices/sos/list`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        let deviceList = response.data.devices;
        
        // Apply additional filters
        if (filter !== 'all' && filter !== 'sos') {
          deviceList = deviceList.filter(d => d.status === filter);
        }
        
        setDevices(deviceList);
      }
    } catch (error) {
      console.error('Error loading devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'rfr': 'bg-green-100 text-green-800',
      'repair_in_progress': 'bg-blue-100 text-blue-800',
      'repair_completed': 'bg-purple-100 text-purple-800',
      're_pna': 'bg-orange-100 text-orange-800',
      'assigned_to_engineer': 'bg-gray-100 text-gray-800',
      'fqc_pass': 'bg-green-100 text-green-800',
      'fqc_fail_rtr': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading devices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Wrench className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">My Devices</h1>
          </div>
          <button
            onClick={() => navigate('/engineer/dashboard')}
            className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-semibold ${
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({devices.length})
            </button>
            <button
              onClick={() => setFilter('sos')}
              className={`px-4 py-2 rounded-lg font-semibold ${
                filter === 'sos'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <AlertCircle className="w-4 h-4 inline mr-1" />
              SOS
            </button>
            <button
              onClick={() => setFilter('rfr')}
              className={`px-4 py-2 rounded-lg font-semibold ${
                filter === 'rfr'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Ready for Repair
            </button>
            <button
              onClick={() => setFilter('repair_in_progress')}
              className={`px-4 py-2 rounded-lg font-semibold ${
                filter === 'repair_in_progress'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              In Progress
            </button>
            <button
              onClick={() => setFilter('repair_completed')}
              className={`px-4 py-2 rounded-lg font-semibold ${
                filter === 'repair_completed'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Completed
            </button>
          </div>
        </div>

        {/* Devices Grid */}
        {devices.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600 text-lg">No devices found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {devices.map((device, index) => {
              const daysInRepair = device.created_at
                ? Math.floor((new Date() - new Date(device.created_at)) / (1000 * 60 * 60 * 24))
                : 0;

              return (
                <div
                  key={index}
                  onClick={() => navigate(`/engineer/repair/${device.trc_device_id}`)}
                  className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer overflow-hidden"
                >
                  {/* Header with SOS indicator */}
                  {device.is_sos && (
                    <div className="bg-red-600 text-white px-4 py-2 text-sm font-semibold flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      SOS PRIORITY
                    </div>
                  )}

                  {/* Device Info */}
                  <div className="p-6">
                    <div className="font-bold text-xl text-gray-900 mb-2">{device.did}</div>
                    <div className="text-gray-600 mb-4">
                      {device.brand} {device.model}
                    </div>

                    {/* Status Badge */}
                    <div className="mb-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(device.status)}`}>
                        {device.status.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </div>

                    {/* Metrics */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">TRC ID:</span>
                        <span className="font-semibold text-gray-900">{device.trc_device_id.substring(0, 15)}...</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Days in Repair:</span>
                        <span className={`font-semibold ${daysInRepair > 7 ? 'text-red-600' : 'text-gray-900'}`}>
                          {daysInRepair}
                        </span>
                      </div>
                      {device.iqc_grade && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">IQC Grade:</span>
                          <span className="font-semibold text-gray-900">{device.iqc_grade}</span>
                        </div>
                      )}
                      {device.customer_complaint && (
                        <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-900">
                          <strong>Complaint:</strong> {device.customer_complaint}
                        </div>
                      )}
                    </div>

                    {/* Action Button */}
                    <button className="mt-4 w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
                      Manage Repair
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EngineerDevices;
